/** @jsxImportSource @emotion/react */
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  css,
  Box,
  Typography,
  TableFooter,
  IconButton,
  Button,
} from '@mui/material';
import React, { ForwardedRef, forwardRef, useCallback, useState, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { TableVirtuoso } from 'react-virtuoso';
import { useNavigate, useParams } from 'react-router-dom';
import useNotifications from '../../../../../hooks/useNotifications';
import { InvoiceWorking, InvoiceWorkingPaginationArgs } from '../../../typings';
import getInvoiceWorkingAPI from '../../../request/get-invoice-working';
import usePaginatedQuery, {
  UsePaginatedQueryResult,
} from '../../../../../hooks/usePaginatedQuery';
import { invoiceWorkingListAtom } from '../../../state';
import Loader from '../../../../../components/loader';
import ProfileCell from '../../../../organisations/components/ProfileCell';
import { INVOICE_WORKING_TABLE_COLUMN_WIDTHS } from '../../../constants';
import { formatCurrency } from '../../../../../utils/currencyFormatter';
import { getChannelDetails } from '../../../../../utils/getSetChannelDetails';
import getOrganisationAPI from '../../../../organisations/request/getOrganisation';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(/\//g, '/');
};

const TableComponents = {
  Scroller: forwardRef((props, ref: ForwardedRef<HTMLDivElement>) => (
    <TableContainer {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table {...props} style={{ borderCollapse: 'separate' }} />
  ),
  TableHead: TableHead as any,
  TableRow: TableRow as any,
  TableBody: forwardRef((props, ref: ForwardedRef<HTMLTableSectionElement>) => (
    <TableBody {...props} ref={ref} />
  )),
  TableFoot: forwardRef((props, ref: ForwardedRef<HTMLTableSectionElement>) => (
    <TableFooter
      {...props}
      ref={ref}
      style={{
        position: 'unset',
      }}
    />
  )),
};

const InvoiceWorkingListTable = () => {
  const navigation = useNavigate();
  const { addNotification } = useNotifications();
  const channelDetails = getChannelDetails();
  const { payoutCommissionId } = useParams<{
    payoutCommissionId: string;
  }>();

  const [companyDetails, setCompanyDetails] = useState<Record<string, any>>({});

  const getter = useCallback(
    async (paginationData?: InvoiceWorkingPaginationArgs) => {
      if (!payoutCommissionId) return { data: [], paginationData: { hasMore: false, totalCount: 0 } };
      const response = await getInvoiceWorkingAPI(payoutCommissionId, paginationData);
      return {
        data: response.data,
        paginationData: response.nextOrg,
      } as UsePaginatedQueryResult<
        InvoiceWorking,
        InvoiceWorkingPaginationArgs
      >;
    },
    [payoutCommissionId]
  );

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      const response = await getOrganisationAPI(channelDetails?.id);
      const details = response.customers?.reduce((acc, company) => {
        acc[company.id] = company;
        return acc;
      }, {} as Record<string, any>);
      setCompanyDetails(details || {});
    };
    fetchCompanyDetails();
  }, [channelDetails?.id]);

  const {
    requestData: { loading, error, data: invoiceWorkingData, isFetchingMore },
    fetchMore: fetchMoreInvoiceWorking,
  } = usePaginatedQuery<InvoiceWorking, InvoiceWorkingPaginationArgs>({
    requestAtom: invoiceWorkingListAtom,
    getter: getter,
    cacheData: false,
  });

  const renderTableRows = () => {
    if (loading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={9}>
              <Box
                css={css`
                  height: 300px;
                  width: 100%;
                `}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'center'}
                alignItems={'center'}
                gap={'8px'}
              >
                <Loader />
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }
    let emptyTableInfo = `No invoice working found`;

    if (error) {
      emptyTableInfo = 'An error occurred. Please try again';
    }
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={9}>
            <Box
              css={css`
                height: 300px;
                margin: auto;
              `}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={'8px'}
            >
              <Typography
                css={css`
                  color: rgba(0, 0, 0, 0.87);
                  font-size: 14px;
                  font-weight: 400;
                `}
              >
                {emptyTableInfo}
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const renderHeader = () => {
    return (
      <TableRow
        css={css`
          background-color: #fff;
        `}
      >
        <TableCell
          css={css`
            width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.NAME.width};
            min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.NAME.minWidth};
            max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.NAME.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Name
          </Typography>
        </TableCell>
        <TableCell
          css={css`
            width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.TYPE.width};
            min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.TYPE.minWidth};
            max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.TYPE.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Type
          </Typography>
        </TableCell>
        <TableCell
          css={css`
            width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DATE.width};
            min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DATE.minWidth};
            max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DATE.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Date
          </Typography>
        </TableCell>
        <TableCell
          css={css`
            width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DEAL_VALUE.width};
            min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DEAL_VALUE.minWidth};
            max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DEAL_VALUE.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Deal Value
          </Typography>
        </TableCell>
        {/* <TableCell
          css={css`
            width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_RATE.width};
            min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_RATE.minWidth};
            max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_RATE.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Commission Rate
          </Typography>
        </TableCell> */}
        {/* <TableCell
          css={css`
            width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_EARNED.width};
            min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_EARNED.minWidth};
            max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_EARNED.maxWidth};
          `}
        >
          <Typography
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: '#2d2d2d';
            `}
          >
            Commission Earned
          </Typography>
        </TableCell> */}
      </TableRow>
    );
  };

  const getDataRowMap = useCallback(
    (index: number, invoiceWorking: InvoiceWorking) => {
      const company = companyDetails[invoiceWorking.orgId];
      return (
        <>
          <TableCell
            component="th"
            scope="row"
            css={css`
              width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.NAME.width};
              min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.NAME.minWidth};
              max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.NAME.maxWidth};
              padding-right: 0px;
            `}
          >
            <Box
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                align-self: stretch;
              `}
            >
              <ProfileCell
                name={company?.name || ''}
                primaryTextStyles={{
                  overflow: 'auto',
                  whiteSpace: 'wrap',
                  wordBreak: 'break-word',
                  fontSize: '14px',
                  fontWeight: '500',
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                  color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                }}
                secondaryText={invoiceWorking.orgId}
                height="40px"
                width="40px"
                profileURL={company?.imageUrl ?? undefined}
              />
            </Box>
          </TableCell>
          <TableCell
            css={css`
              width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.TYPE.width};
              min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.TYPE.minWidth};
              max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.TYPE.maxWidth};
            `}
          >
            {invoiceWorking.planId}
          </TableCell>
          <TableCell
            css={css`
              width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DATE.width};
              min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DATE.minWidth};
              max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DATE.maxWidth};
            `}
          >
            {formatDate(invoiceWorking.date)}
          </TableCell>
          <TableCell
            css={css`
              width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DEAL_VALUE.width};
              min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DEAL_VALUE.minWidth};
              max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.DEAL_VALUE.maxWidth};
            `}
          >
            {formatCurrency(invoiceWorking.amount, invoiceWorking.currencyCode)}
          </TableCell>
          {/* <TableCell
            css={css`
              width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_RATE.width};
              min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_RATE.minWidth};
              max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_RATE.maxWidth};
            `}
          >
            {invoiceWorking.commissionRate}
          </TableCell> */}
          {/* <TableCell
            css={css`
              width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_EARNED.width};
              min-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_EARNED.minWidth};
              max-width: ${INVOICE_WORKING_TABLE_COLUMN_WIDTHS.COMMISSION_EARNED.maxWidth};
            `}
          >
            {invoiceWorking.commissionAmount}
          </TableCell> */}
        </>
      );
    },
    [companyDetails]
  );

  const renderLoaderInFooter = () => {
    if (isFetchingMore) {
      return (
        <TableRow>
          <TableCell colSpan={7}>
            <Box
              css={css`
                height: 50px;
                display: flex;
                justify-content: center;
                align-items: center;
              `}
            >
              <Loader />
            </Box>
          </TableCell>
        </TableRow>
      );
    }
    return null;
  };

  if (
    !loading &&
    !error &&
    Array.isArray(invoiceWorkingData) &&
    invoiceWorkingData.length > 0
  ) {
    return (
      <>
        <TableVirtuoso
          style={{
            height: `73vh`,
            width: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fff',
          }}
          css={css`
            &::-webkit-scrollbar {
              display: none; /* Hide scrollbar for WebKit browsers */
            }
            -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
            scrollbar-width: none; /* Hide scrollbar for Firefox */
          `}
          data={invoiceWorkingData}
          fixedHeaderContent={renderHeader}
          totalCount={(invoiceWorkingData ?? []).length}
          itemContent={getDataRowMap}
          endReached={fetchMoreInvoiceWorking}
          components={{
            ...TableComponents,
            EmptyPlaceholder: () => <>...</>,
          }}
          fixedFooterContent={renderLoaderInFooter}
        />
      </>
    );
  }

  return (
    <TableContainer
      component={Paper}
      css={css`
        border-radius: 12px;
      `}
    >
      <Table sx={{ minWidth: 650 }} aria-label="org table">
        <TableHead>{renderHeader()}</TableHead>
        {renderTableRows()}
      </Table>
    </TableContainer>
  );
};

export default InvoiceWorkingListTable;
